import { useState } from 'react';
import { floors } from '@/content/floors';
import type { Progress } from '@/game/progress';
import {
  createProfile,
  deleteProfile,
  listProfiles,
  renameProfile,
  snapshotOf,
  switchProfile,
  type Profile,
} from '@/game/profiles';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card } from './ui';
import { fmtTime, useToast } from './util';

/** The game state a profile holds, summarised for the list. */
function summary(id: string) {
  const snap = snapshotOf(id);
  let progress: Progress = {};
  let age: number | null = null;
  try {
    progress = JSON.parse(snap['foe-names.progress.v1'] ?? '{}') as Progress;
    age = (JSON.parse(snap['foe-names.settings.v1'] ?? '{}') as { age?: number }).age ?? null;
  } catch {
    // unreadable state: shown as empty
  }
  const stars = floors.reduce((sum, f) => sum + (progress[f.id]?.stars ?? 0), 0);
  const done = floors.filter((f) => (progress[f.id]?.completions ?? 0) > 0).length;
  return { age, stars, done, intro: snap['foe-names.intro.v1'] === 'seen' };
}

/** Reloads into the profiles tab, so every store loads the new game state. */
function reload() {
  window.history.replaceState(null, '', '#/debug/profiles');
  window.location.reload();
}

/**
 * Player profiles (game/profiles.ts): start a new player, go back to an earlier one, rename or
 * delete. Each profile is a whole game state (progress, age, intro, story beats).
 */
export function ProfilesPanel() {
  const toast = useToast();
  const [list, setList] = useState(listProfiles);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const refresh = () => {
    setList(listProfiles());
  };
  const sorted = [...list.profiles].sort((a, b) =>
    a.id === list.active ? -1 : b.id === list.active ? 1 : b.usedAt - a.usedAt,
  );

  const create = (copy: boolean) => {
    createProfile(name, copy);
    reload();
  };
  const saveName = () => {
    if (!editing) return;
    renameProfile(editing.id, editing.name);
    setEditing(null);
    refresh();
    toast('Renamed');
  };

  return (
    <>
      <Card title="New profile">
        <form
          className={s.row}
          onSubmit={(e) => {
            e.preventDefault();
            create(false);
          }}
        >
          <label htmlFor="profile-name" className={s.visuallyHidden}>
            Name of the new profile
          </label>
          <input
            id="profile-name"
            className={s.input}
            placeholder={`Player ${String(list.profiles.length + 1)}`}
            value={name}
            maxLength={40}
            onChange={(e) => {
              setName(e.target.value);
            }}
            style={{ flex: '1 1 180px' }}
          />
          <button type="submit" className={cx(s.btn, s.primary)}>
            Start new player
          </button>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              create(true);
            }}
          >
            Copy current
          </button>
        </form>
        <p className={s.hint}>
          A new player starts empty: the intro plays and the age is asked. The current game is saved
          in its profile first. Switching reloads the page.
        </p>
      </Card>

      <Card title={`Profiles (${String(list.profiles.length)})`} flush>
        <div className={s.tableWrap}>
          <table className={cx(s.table, s.stack)}>
            <thead>
              <tr>
                <th>Name</th>
                <th className={s.num}>Age</th>
                <th className={s.num}>Stars</th>
                <th className={s.num}>Floors</th>
                <th className={s.hideNarrow}>Intro</th>
                <th className={s.hideNarrow}>Last used</th>
                <th>
                  <span className={s.visuallyHidden}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <ProfileRow
                  key={p.id}
                  profile={p}
                  active={p.id === list.active}
                  editing={editing?.id === p.id ? editing.name : null}
                  confirming={confirmDelete === p.id}
                  onEdit={(value) => {
                    setEditing(value === null ? null : { id: p.id, name: value });
                  }}
                  onSaveName={saveName}
                  onSwitch={() => {
                    switchProfile(p.id);
                    reload();
                  }}
                  onDelete={(confirmed) => {
                    if (!confirmed) {
                      setConfirmDelete(p.id);
                      return;
                    }
                    setConfirmDelete(null);
                    if (deleteProfile(p.id)) reload();
                    else {
                      refresh();
                      toast(`Deleted ${p.name}`);
                    }
                  }}
                  onCancelDelete={() => {
                    setConfirmDelete(null);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function ProfileRow({
  profile,
  active,
  editing,
  confirming,
  onEdit,
  onSaveName,
  onSwitch,
  onDelete,
  onCancelDelete,
}: {
  profile: Profile;
  active: boolean;
  editing: string | null;
  confirming: boolean;
  onEdit: (value: string | null) => void;
  onSaveName: () => void;
  onSwitch: () => void;
  onDelete: (confirmed: boolean) => void;
  onCancelDelete: () => void;
}) {
  const info = summary(profile.id);
  return (
    <tr className={cx(active && s.current)}>
      <td>
        {editing !== null ? (
          <form
            className={s.row}
            onSubmit={(e) => {
              e.preventDefault();
              onSaveName();
            }}
          >
            <input
              className={s.input}
              aria-label={`New name for ${profile.name}`}
              value={editing}
              maxLength={40}
              autoFocus
              onChange={(e) => {
                onEdit(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') onEdit(null);
              }}
            />
            <button type="submit" className={cx(s.btn, s.small, s.primary)}>
              Save
            </button>
            <button
              type="button"
              className={cx(s.btn, s.small)}
              onClick={() => {
                onEdit(null);
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <span className={s.row}>
            <strong>{profile.name}</strong>
            {active && <span className={cx(s.badge, s.badgeAccent)}>playing</span>}
          </span>
        )}
      </td>
      <td data-label="Age" className={s.num}>
        {info.age ?? '–'}
      </td>
      <td data-label="Stars" className={s.num}>
        {info.stars}
      </td>
      <td data-label="Floors" className={s.num}>
        {info.done}/{floors.length}
      </td>
      <td data-label="Intro" className={s.hideNarrow}>
        {info.intro ? (
          <span className={cx(s.badge, s.badgeOk)}>seen</span>
        ) : (
          <span className={s.badge}>not yet</span>
        )}
      </td>
      <td data-label="Last used" className={s.hideNarrow}>
        {fmtTime(profile.usedAt)}
      </td>
      <td>
        <div className={s.row} style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          {confirming ? (
            <>
              <span>Delete {profile.name}?</span>
              <button
                type="button"
                className={cx(s.btn, s.small, s.dangerSolid)}
                onClick={() => {
                  onDelete(true);
                }}
              >
                Delete
              </button>
              <button type="button" className={cx(s.btn, s.small)} onClick={onCancelDelete}>
                Keep
              </button>
            </>
          ) : (
            <>
              {!active && (
                <button type="button" className={cx(s.btn, s.small, s.primary)} onClick={onSwitch}>
                  Switch
                </button>
              )}
              <button
                type="button"
                className={cx(s.btn, s.small)}
                aria-label={`Rename ${profile.name}`}
                onClick={() => {
                  onEdit(profile.name);
                }}
              >
                Rename
              </button>
              <button
                type="button"
                className={cx(s.btn, s.small, s.danger)}
                aria-label={`Delete ${profile.name}`}
                onClick={() => {
                  onDelete(false);
                }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
